import React from 'react';
import './featureFlagSync';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  UserSettingsAuthProviders,
  UserSettingsFeatureFlags,
  UserSettingsGeneral,
  SettingsLayout,
  userSettingsPlugin,
} from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  RequirePermission,
  usePermission,
} from '@backstage/plugin-permission-react';
import { BACKOFFICE_PERMISSIONS } from '@internal/backoffice-auth-common';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  configApiRef,
  createRoutableExtension,
  googleAuthApiRef,
} from '@backstage/core-plugin-api';
import { SignInPage } from '@backstage/core-components';

import type { IdentityApi } from '@backstage/core-plugin-api';
import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { setTokenCookie } from './cookieAuth';

import { FeatureFlagged } from '@backstage/core-app-api';
import { EnginesPage } from '@internal/plugin-engines';
import { EnginesOldPage } from '@internal/plugin-engines-old';
import { AdminPage, InstallationsPage } from '@internal/plugin-installations';
import { OrganisationsPage } from '@internal/plugin-organisations';
import { UsersPage } from '@internal/plugin-users';
import { InsightsPage } from '@internal/plugin-insights';
import { RoutingPage } from '@internal/plugin-routing';
import { WebhookTesterPage } from '@internal/plugin-customer-state-sync';
import { HomePage } from './components/home/HomePage';
import { BillingPage } from '@internal/plugin-billing';
import { JobsPage } from '@internal/plugin-jobs';
import { CostsPage } from '@internal/plugin-costs';
import { WorkflowsPage } from '@internal/plugin-workflows';

import { ThemeProvider } from '@material-ui/core';
import { deepcTheme } from './Theme';
import { themes, UnifiedThemeProvider } from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import { TatreportsPage } from 'backstage-plugin-tatreports';
import { PolicyPage } from 'backstage-plugin-policy';

const SettingsContent = () => {
  const featureFlags = usePermission({
    permission: BACKOFFICE_PERMISSIONS.toggle_feature_flags,
  });
  return (
    <SettingsLayout>
      <SettingsLayout.Route path="general" title="General">
        <UserSettingsGeneral />
      </SettingsLayout.Route>
      <SettingsLayout.Route
        path="auth-providers"
        title="Authentication Providers"
      >
        <UserSettingsAuthProviders />
      </SettingsLayout.Route>
      {featureFlags.allowed && (
        <SettingsLayout.Route path="feature-flags" title="Feature Flags">
          <UserSettingsFeatureFlags />
        </SettingsLayout.Route>
      )}
    </SettingsLayout>
  );
};

// SidebarSettings resolves the user-settings route ref. A plain page at
// /settings leaves that ref without a path, and the shell throws after sign-in.
const SettingsPage = userSettingsPlugin.provide(
  createRoutableExtension({
    name: 'SettingsPage',
    mountPoint: userSettingsPlugin.routes.settingsPage,
    component: () => Promise.resolve(SettingsContent),
  }),
);

const app = createApp({
  featureFlags: [
    {
      pluginId: 'customer-state-sync',
      name: 'jira-sync',
      description:
        'Show the JIRA Sync tester page for triggering and inspecting the scheduled Jira webhook sync.',
    },
    {
      pluginId: 'installations',
      name: 'gate-config-wip-tabs',
      description:
        'Show the unfinished Gate configuration tabs (User authentications, Worklist integration, Use AI reporting). They are UI mockups and nothing there is saved.',
    },
    {
      pluginId: 'installations',
      name: 'gate-config-ui',
      description:
        'Show the structured Gate configuration UI and its Structured/YAML toggle. Off by default: only the raw YAML editor is available.',
    },
    {
      pluginId: 'installations',
      name: 'admin-page',
      description:
        'Show the Admin page. Opening it also requires permission to manage Backoffice users.',
    },
  ],
  themes: [
    {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider children={children} theme={themes.dark} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const discoveryApi = useApi(discoveryApiRef);
      const allowGuest =
        configApi.getOptionalBoolean('auth.signInPage.allowGuest') ?? false;

      return (
        <SignInPage
          align="center"
          {...props}
          auto={!allowGuest}
          providers={
            allowGuest
              ? ['guest']
              : [
                  {
                    id: 'google',
                    title: 'Google',
                    message: 'Sign in using Google',
                    apiRef: googleAuthApiRef,
                  },
                ]
          }
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApi,
            );

            props.onSignInSuccess(identityApi);
          }}
        />
      );
    },
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<SettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <FeatureFlagged with="engines-next">
      <Route path="/engines-next" element={<EnginesPage />} />
    </FeatureFlagged>
    <FeatureFlagged without="engines-next">
      <Route path="/engines" element={<EnginesOldPage />} />
    </FeatureFlagged>
    <Route path="/workflows" element={<WorkflowsPage />} />
    <Route path="/installations" element={<InstallationsPage />} />
    <FeatureFlagged with="admin-page">
      <Route
        path="/admin"
        element={
          <RequirePermission
            permission={BACKOFFICE_PERMISSIONS.manage_backoffice_users}
          >
            <AdminPage />
          </RequirePermission>
        }
      />
    </FeatureFlagged>
    <Route path="/organisations" element={<OrganisationsPage />} />
    <Route path="/users" element={<UsersPage />} />
    <Route path="/insights" element={<InsightsPage />} />
    <Route path="/routing" element={<RoutingPage />} />
    <FeatureFlagged with="jira-sync">
      <Route
        path="/customer-state-sync-tester"
        element={<WebhookTesterPage />}
      />
    </FeatureFlagged>
    <Route path="/billing" element={<BillingPage />} />
    <Route path="/jobs" element={<JobsPage />} />
    <Route path="/costs" element={<CostsPage />} />
    <Route path="/tatreports" element={<TatreportsPage />} />
    <Route path="/policy" element={<PolicyPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <ThemeProvider theme={deepcTheme}>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </ThemeProvider>,
);
